import {
  DOCUMENT_ASSEMBLER_LIMITS,
  IDocumentAssemblerTemplateVersionDetail,
  updateDocumentAssemblerTemplateVersionGuidance,
} from '@commenda/api';
import { Alert, Button, Textarea } from '@commenda/kakashi';
import { messageFromApiErrorResponse } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../hooks/useNotification';

import type { DocumentAssemblerVersionLifecycle } from './document-assembler.types';

interface DocumentAssemblerAuthorGuidanceCardProps {
  templateId: string;
  version: IDocumentAssemblerTemplateVersionDetail;
  lifecycle: DocumentAssemblerVersionLifecycle;
  onSaved?: () => Promise<void>;
}

export const DocumentAssemblerAuthorGuidanceCard = ({
  templateId,
  version,
  lifecycle,
  onSaved,
}: DocumentAssemblerAuthorGuidanceCardProps) => {
  const { setNotification } = useNotification();
  const [authorGuidance, setAuthorGuidance] = useState(
    version.authorGuidance ?? ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // '' is a legal value and is how you clear it, so set-to-empty counts as a change.
  const hasChanged =
    authorGuidance.trim() !== (version.authorGuidance ?? '').trim();
  const isOverLimit =
    authorGuidance.length > DOCUMENT_ASSEMBLER_LIMITS.maxAuthorGuidanceLength;

  const onSave = async () => {
    setSaveError(null);
    setIsSaving(true);

    try {
      const response = await updateDocumentAssemblerTemplateVersionGuidance(
        templateId,
        version.id,
        authorGuidance.trim()
      );

      if (!response.ok) {
        setSaveError(
          await messageFromApiErrorResponse(
            response,
            'The writing conventions could not be saved.'
          )
        );
        return;
      }

      setNotification({
        show: true,
        valid: true,
        title: 'Writing conventions saved',
      });
      await onSaved?.();
    } catch (error: unknown) {
      // Covers both the raw-fetch rejection above and a rejecting onSaved refresh.
      setSaveError(
        error instanceof Error
          ? error.message
          : 'The writing conventions could not be saved. Try again in a moment.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
      <div className="flex flex-col gap-xs">
        <h3 className="txt-medium-medium text-text-primary">
          Writing conventions and additional instructions
        </h3>
        <p className="txt-extra-small-regular text-text-secondary">
          You can add any additional instructions to be passed to the AI agent
          for this version here. For example, how defined terms are styled,
          tense, how the parties are referred to, date and percentage formats.
          They stay in front of the expert for the whole run and are restated
          each time it writes values. It cannot finish the document without
          checking what it wrote against them.
        </p>
      </div>

      {saveError ? (
        <Alert variant="destructive" description={saveError} />
      ) : null}

      <Alert variant="info" description={lifecycle.conventionsNotice} />

      {lifecycle.isEditable ? (
        <>
          <Textarea
            rows={8}
            value={authorGuidance}
            onChange={(event) => setAuthorGuidance(event.target.value)}
            error={
              isOverLimit
                ? `${authorGuidance.length.toLocaleString()} characters — the limit is ${DOCUMENT_ASSEMBLER_LIMITS.maxAuthorGuidanceLength.toLocaleString()}.`
                : undefined
            }
          />
          <div className="flex items-center justify-between gap-md">
            <span className="txt-extra-small-regular text-text-tertiary">
              {authorGuidance.length.toLocaleString()} /{' '}
              {DOCUMENT_ASSEMBLER_LIMITS.maxAuthorGuidanceLength.toLocaleString()}
            </span>
            <Button
              variant="primary"
              size="sm"
              disabled={!hasChanged || isOverLimit || isSaving}
              isLoading={isSaving}
              onClick={onSave}
            >
              Save conventions
            </Button>
          </div>
        </>
      ) : version.authorGuidance ? (
        <p className="txt-small-regular whitespace-pre-wrap text-text-primary">
          {version.authorGuidance}
        </p>
      ) : (
        <p className="txt-small-regular text-text-tertiary">None declared.</p>
      )}
    </div>
  );
};
